import React, { useState, useEffect } from 'react';

const AnalyticsChart = ({ isCompact = false }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const workerUrl = 'https://empty-sunset-e6ad.avinash8898.workers.dev';
        const response = await fetch(workerUrl);
        const data = await response.json();

        if (data.error) {
          setError(data.error);
        } else {
          setAnalytics(data);
        }
      } catch (err) {
        console.error('Analytics fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className={`backdrop-blur-xl bg-gradient-to-br from-gray-900/60 to-gray-800/40 border border-white/10 rounded-lg shadow-2xl ${isCompact ? 'p-4' : 'p-8'} text-white`}>
        <p className={`text-center ${isCompact ? 'text-xs' : 'text-sm'}`}>📊 Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`backdrop-blur-xl bg-gradient-to-br from-gray-900/60 to-gray-800/40 border border-red-500/20 rounded-lg shadow-2xl ${isCompact ? 'p-4' : 'p-8'} text-white`}>
        <p className={`text-center text-red-400 ${isCompact ? 'text-xs' : 'text-sm'}`}>⚠️ Error</p>
      </div>
    );
  }

  const metrics = [
    {
      label: 'Bot Requests',
      value: analytics?.totalRequests || 0,
      color: 'from-pink-400 to-pink-500',
      max: 5000,
    },
    {
      label: 'Visitors',
      value: analytics?.uniqueVisitors || 0,
      color: 'from-yellow-400 to-yellow-500',
      max: 1000,
    },
  ];

  const maxValue = Math.max(...metrics.map(m => m.value));

  if (isCompact) {
    return (
      <div className="backdrop-blur-xl bg-gradient-to-br from-gray-900/60 to-gray-800/40 border border-white/10 rounded-lg shadow-2xl p-4">
        <h3 className="text-sm font-bold tracking-tight text-white bg-clip-text text-transparent bg-gradient-to-r from-yellow-200 to-pink-500 mb-3">
          Website Analytics
        </h3>

        <div className="space-y-3">
          {metrics.map((metric, index) => (
            <div key={index} className="space-y-1">
              <div className="flex justify-between items-center">
                <label className="text-white font-semibold text-xs">
                  {metric.label}
                </label>
                <span className={`text-xs font-bold bg-clip-text text-transparent bg-gradient-to-r ${metric.color}`}>
                  {metric.value.toLocaleString()}
                </span>
              </div>

              {/* Horizontal Bar */}
              <div className="w-full bg-white/5 border border-white/10 rounded-full h-4 overflow-hidden">
                <div
                  className={`h-full bg-gradient-to-r ${metric.color} transition-all duration-500`}
                  style={{
                    width: `${(metric.value / (maxValue > 0 ? maxValue : 1)) * 100}%`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Last Updated */}
        <div className="mt-3 pt-3 border-t border-white/10 text-center">
          <p className="text-xs text-gray-500">
            Updated: {new Date(analytics?.timestamp).toLocaleTimeString()}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto py-12 px-4">
      <div className="backdrop-blur-xl bg-gradient-to-br from-gray-900/60 to-gray-800/40 border border-white/10 rounded-lg shadow-2xl p-8">
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white bg-clip-text text-transparent bg-gradient-to-r from-yellow-200 to-pink-500 mb-8">
          Site Analytics
        </h2>

        <div className="space-y-8">
          {metrics.map((metric, index) => (
            <div key={index} className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-white font-semibold text-lg">
                  {metric.label}
                </label>
                <span className={`text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r ${metric.color}`}>
                  {metric.value.toLocaleString()}
                </span>
              </div>

              {/* Horizontal Bar */}
              <div className="w-full bg-white/5 border border-white/10 rounded-full h-8 overflow-hidden">
                <div
                  className={`h-full bg-gradient-to-r ${metric.color} transition-all duration-500 flex items-center justify-end pr-3`}
                  style={{
                    width: `${(metric.value / (maxValue > 0 ? maxValue : 1)) * 100}%`,
                  }}
                >
                  <span className="text-white text-xs font-bold">
                    {((metric.value / (maxValue > 0 ? maxValue : 1)) * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Last Updated */}
        <div className="mt-8 pt-6 border-t border-white/10 text-center">
          <p className="text-sm text-gray-400">
            Last updated: {new Date(analytics?.timestamp).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsChart;
